import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import ZButton from "@/components/ZButton";
import { ZInput, ZSelect } from "@/components/ZInput";
import Modal from "@/components/Modal";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Save, Users, Search, Download, Share2, Copy, Check,
  LayoutGrid, List as ListIcon, Ticket, UserPlus, Circle as CircleIcon, Square,
} from "lucide-react";

const COLORS = ["#01696F", "#DA7101", "#A12C7B", "#3B82F6", "#10B981", "#EF4444", "#F59E0B", "#6366F1"];
const uuid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

function tableRadius(seats) { return Math.max(30, Math.min(64, 24 + seats * 2.4)); }
function rectSize(seats) { return { w: Math.max(80, Math.min(280, 56 + seats * 9)), h: 46 }; }

export default function OrgTablePlanPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const svgRef = useRef(null);
  const [event, setEvent] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("plan"); // plan | list
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [guestSearch, setGuestSearch] = useState("");
  const [showAddTable, setShowAddTable] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tableForm, setTableForm] = useState({ label: "", shape: "round", seats: 8, color: COLORS[0] });
  const [guestForm, setGuestForm] = useState({ name: "", party_size: 1 });
  const drag = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [eR, pR] = await Promise.all([
          api.get(`/events/${id}`),
          api.get(`/events/${id}/table-plan`),
        ]);
        if (cancelled) return;
        setEvent(eR.data);
        setPlan(pR.data);
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const occupancy = useMemo(() => {
    const map = {};
    (plan?.guests || []).forEach((g) => {
      if (g.table_id) map[g.table_id] = (map[g.table_id] || 0) + (g.party_size || 1);
    });
    return map;
  }, [plan]);

  const guestsByTable = (tid) => (plan?.guests || []).filter((g) => g.table_id === tid);
  const unassigned = (plan?.guests || []).filter((g) => !g.table_id);
  const seated = (plan?.guests || []).filter((g) => g.table_id).length;

  const filteredGuests = (plan?.guests || []).filter((g) =>
    !guestSearch.trim() || g.name.toLowerCase().includes(guestSearch.trim().toLowerCase()));

  // ---- mutations (local) ----
  const setPlanField = (patch) => setPlan((p) => ({ ...p, ...patch }));
  const updateTable = (tid, patch) => setPlan((p) => ({ ...p, tables: p.tables.map((t) => t.id === tid ? { ...t, ...patch } : t) }));
  const removeTable = (tid) => setPlan((p) => ({
    ...p,
    tables: p.tables.filter((t) => t.id !== tid),
    guests: p.guests.map((g) => g.table_id === tid ? { ...g, table_id: null } : g),
  }));
  const assignGuest = (gid, tid) => setPlan((p) => ({ ...p, guests: p.guests.map((g) => g.id === gid ? { ...g, table_id: tid || null } : g) }));
  const removeGuest = (gid) => setPlan((p) => ({ ...p, guests: p.guests.filter((g) => g.id !== gid) }));

  const addTable = () => {
    if (!tableForm.label.trim()) return toast.error("Nom de table obligatoire.");
    const n = plan.tables.length;
    const newTable = {
      id: uuid(),
      label: tableForm.label.trim(),
      shape: tableForm.shape,
      seats: parseInt(tableForm.seats) || 8,
      color: tableForm.color,
      x: 120 + (n % 4) * 180,
      y: 120 + Math.floor(n / 4) * 160,
    };
    setPlan((p) => ({ ...p, tables: [...p.tables, newTable] }));
    setShowAddTable(false);
    setTableForm({ label: "", shape: "round", seats: 8, color: COLORS[(n + 1) % COLORS.length] });
  };

  const addGuest = () => {
    if (!guestForm.name.trim()) return;
    setPlan((p) => ({ ...p, guests: [...p.guests, { id: uuid(), name: guestForm.name.trim(), party_size: parseInt(guestForm.party_size) || 1, table_id: null, ticket_id: null, note: "" }] }));
    setGuestForm({ name: "", party_size: 1 });
  };

  const importTickets = async () => {
    try {
      const r = await api.post(`/events/${id}/table-plan/import-tickets`);
      setPlan(r.data);
      toast.success(r.data.imported > 0 ? `${r.data.imported} invité(s) importé(s).` : "Aucun nouveau billet à importer.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async (silent = false) => {
    setSaving(true);
    try {
      const payload = {
        width: plan.width, height: plan.height, is_public: plan.is_public,
        tables: plan.tables, guests: plan.guests.map((g) => ({ ...g, note: g.note || "" })),
      };
      const r = await api.put(`/events/${id}/table-plan`, payload);
      setPlan(r.data);
      if (!silent) toast.success("Plan de table enregistré.");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const togglePublic = async () => {
    const next = !plan.is_public;
    setPlanField({ is_public: next });
    // persist immediately so the share link works
    try {
      const r = await api.put(`/events/${id}/table-plan`, {
        width: plan.width, height: plan.height, is_public: next, tables: plan.tables, guests: plan.guests,
      });
      setPlan(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copyLink = async () => {
    if (!plan?.public_url) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(plan.public_url);
      } else {
        throw new Error("clipboard unavailable");
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback : sélectionner le champ pour copie manuelle
      const el = document.querySelector('[data-testid="share-link"]');
      if (el) { el.focus(); el.select(); }
      toast.message("Copie automatique indisponible — copiez le lien manuellement (Ctrl/Cmd+C).");
    }
  };

  // ---- drag tables ----
  const toSvg = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX; pt.y = clientY;
    const m = svg.getScreenCTM().inverse();
    const p = pt.matrixTransform(m);
    return { x: p.x, y: p.y };
  };
  const onTablePointerDown = (e, t) => {
    e.stopPropagation();
    setSelectedTableId(t.id);
    const { x, y } = toSvg(e.clientX, e.clientY);
    drag.current = { id: t.id, dx: x - t.x, dy: y - t.y };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const { x, y } = toSvg(e.clientX, e.clientY);
    updateTable(drag.current.id, {
      x: Math.max(20, Math.min(plan.width - 20, x - drag.current.dx)),
      y: Math.max(20, Math.min(plan.height - 20, y - drag.current.dy)),
    });
  };
  const onPointerUp = () => { drag.current = null; };

  if (loading) return <div className="z-container" style={{ textAlign: "center", paddingTop: 60 }}><span className="z-loader z-loader-dark" /></div>;
  if (!plan) return null;

  return (
    <div className="z-container" data-testid="org-table-plan-page" style={{ maxWidth: 1100 }}>
      <button onClick={() => nav(`/org/event/${id}`)} className="z-btn z-btn-ghost z-btn-sm" data-testid="table-plan-back" style={{ marginBottom: 8 }}>
        <ArrowLeft size={16} /> Retour
      </button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 className="z-page-title" style={{ marginBottom: 2 }}>Plan de table</h1>
          <p className="z-page-sub" style={{ margin: 0 }}>{event?.title} · {seated}/{plan.guests.length} invité(s) placé(s)</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <ZButton variant="outline" size="sm" onClick={() => setShowShare(true)} data-testid="table-plan-share-btn"><Share2 size={15} /> Partager</ZButton>
          <ZButton variant="outline" size="sm" onClick={() => window.print()} data-testid="table-plan-print-btn"><Download size={15} /> PDF / Imprimer</ZButton>
          <ZButton size="sm" loading={saving} onClick={() => save(false)} data-testid="table-plan-save-btn"><Save size={15} /> Enregistrer</ZButton>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "14px 0 16px", alignItems: "center" }}>
        <ZButton size="sm" onClick={() => setShowAddTable(true)} data-testid="add-table-btn"><Plus size={15} /> Ajouter une table</ZButton>
        <ZButton variant="outline" size="sm" onClick={importTickets} data-testid="import-tickets-btn"><Ticket size={15} /> Importer les billets</ZButton>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", gap: 4, background: "var(--z-bg-soft,#f1f1f1)", borderRadius: 999, padding: 3 }}>
          <button className={`z-chip ${view === "plan" ? "z-chip-active" : ""}`} onClick={() => setView("plan")} data-testid="view-plan-btn"><LayoutGrid size={14} /> Plan</button>
          <button className={`z-chip ${view === "list" ? "z-chip-active" : ""}`} onClick={() => setView("list")} data-testid="view-list-btn"><ListIcon size={14} /> Liste</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 18, alignItems: "start" }} className="table-plan-grid">
        {/* Canvas / List */}
        <div className="z-card" style={{ padding: 12, overflow: "auto" }} data-testid="table-plan-canvas-wrap">
          {view === "plan" ? (
            plan.tables.length === 0 ? (
              <div style={{ textAlign: "center", padding: 50, color: "var(--z-muted)" }}>Ajoutez votre première table pour commencer.</div>
            ) : (
              <svg
                ref={svgRef} viewBox={`0 0 ${plan.width} ${plan.height}`}
                style={{ width: "100%", minWidth: 600, background: "repeating-linear-gradient(0deg,#fafafa,#fafafa 24px,#f2f2f2 24px,#f2f2f2 25px)", borderRadius: 12, touchAction: "none" }}
                onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
                data-testid="table-plan-svg"
              >
                {plan.tables.map((t) => {
                  const occ = occupancy[t.id] || 0;
                  const over = occ > t.seats;
                  const isSel = selectedTableId === t.id;
                  const stroke = isSel ? "#111" : (over ? "#EF4444" : t.color);
                  return (
                    <g key={t.id} onPointerDown={(e) => onTablePointerDown(e, t)} style={{ cursor: "grab" }} data-testid={`table-node-${t.id}`}>
                      {t.shape === "round" ? (
                        <circle cx={t.x} cy={t.y} r={tableRadius(t.seats)} fill={`${t.color}22`} stroke={stroke} strokeWidth={isSel ? 3 : 2} />
                      ) : (
                        (() => { const { w, h } = rectSize(t.seats); return (
                          <rect x={t.x - w / 2} y={t.y - h / 2} width={w} height={h} rx={8} fill={`${t.color}22`} stroke={stroke} strokeWidth={isSel ? 3 : 2} />
                        ); })()
                      )}
                      <text x={t.x} y={t.y - 5} textAnchor="middle" fontSize="14" fontWeight="700" fill="#111">{t.label}</text>
                      <text x={t.x} y={t.y + 15} textAnchor="middle" fontSize="14" fontWeight="700" fill={over ? "#EF4444" : "#01696F"}>{occ}/{t.seats}</text>
                    </g>
                  );
                })}
              </svg>
            )
          ) : (
            <div data-testid="table-plan-list">
              {plan.tables.map((t) => {
                const occ = occupancy[t.id] || 0;
                return (
                  <div key={t.id} style={{ marginBottom: 14, borderLeft: `4px solid ${t.color}`, paddingLeft: 12 }} data-testid={`list-table-${t.id}`}>
                    <div style={{ fontWeight: 700 }}>{t.label} <span style={{ color: occ > t.seats ? "var(--z-error)" : "var(--z-muted)", fontSize: 13, fontWeight: 500 }}>· {occ}/{t.seats} ({t.shape === "round" ? "ronde" : "rectangulaire"})</span></div>
                    {guestsByTable(t.id).length === 0 ? (
                      <div style={{ fontSize: 13, color: "var(--z-muted)" }}>—</div>
                    ) : (
                      <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 14 }}>
                        {guestsByTable(t.id).map((g) => <li key={g.id}>{g.name}{g.party_size > 1 ? ` (+${g.party_size - 1})` : ""}</li>)}
                      </ul>
                    )}
                  </div>
                );
              })}
              {unassigned.length > 0 && (
                <div style={{ marginTop: 16, color: "var(--z-orange)" }} data-testid="list-unassigned">
                  <div style={{ fontWeight: 700 }}>Non placés ({unassigned.length})</div>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 14 }}>{unassigned.map((g) => <li key={g.id}>{g.name}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          {/* Selected table editor */}
          {view === "plan" && selectedTableId && (() => {
            const t = plan.tables.find((x) => x.id === selectedTableId);
            if (!t) return null;
            return (
              <div style={{ marginTop: 12, padding: 12, background: "var(--z-bg-soft,#f7f7f7)", borderRadius: 10 }} data-testid="table-editor">
                <div style={{ display: "flex", gap: 8, alignItems: "end", flexWrap: "wrap" }}>
                  <ZInput label="Nom" value={t.label} onChange={(e) => updateTable(t.id, { label: e.target.value })} data-testid="table-edit-label" />
                  <ZSelect label="Forme" value={t.shape} onChange={(e) => updateTable(t.id, { shape: e.target.value })} data-testid="table-edit-shape">
                    <option value="round">Ronde</option>
                    <option value="rect">Rectangulaire</option>
                  </ZSelect>
                  <ZInput label="Couverts" type="number" min="1" max="60" value={t.seats} onChange={(e) => updateTable(t.id, { seats: parseInt(e.target.value) || 1 })} data-testid="table-edit-seats" />
                  <button className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error)", marginBottom: 14 }} onClick={() => { removeTable(t.id); setSelectedTableId(null); }} data-testid="table-edit-delete"><Trash2 size={15} /></button>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Guests panel */}
        <div className="z-card" style={{ padding: 14 }} data-testid="guests-panel">
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, marginBottom: 10 }}><Users size={16} /> Invités ({plan.guests.length})</div>

          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <ZInput placeholder="Nom de l'invité" value={guestForm.name} onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })} data-testid="guest-name-input" />
            <ZInput type="number" min="1" max="30" value={guestForm.party_size} onChange={(e) => setGuestForm({ ...guestForm, party_size: e.target.value })} data-testid="guest-party-input" style={{ width: 70 }} />
            <ZButton size="sm" onClick={addGuest} data-testid="add-guest-btn"><UserPlus size={15} /></ZButton>
          </div>

          <div style={{ position: "relative", marginBottom: 10 }}>
            <Search size={15} style={{ position: "absolute", left: 10, top: 11, color: "var(--z-muted)" }} />
            <input className="z-input" style={{ paddingLeft: 32 }} placeholder="Rechercher un invité…" value={guestSearch} onChange={(e) => setGuestSearch(e.target.value)} data-testid="guest-search-input" />
          </div>

          <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            {filteredGuests.length === 0 && <div style={{ fontSize: 13, color: "var(--z-muted)", textAlign: "center", padding: 10 }}>Aucun invité.</div>}
            {filteredGuests.map((g) => (
              <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, padding: "6px 8px", background: g.table_id ? "rgba(1,105,111,0.06)" : "rgba(218,113,1,0.06)", borderRadius: 8 }} data-testid={`guest-row-${g.id}`}>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {g.name}{g.party_size > 1 ? ` (+${g.party_size - 1})` : ""}
                </span>
                <select
                  value={g.table_id || ""}
                  onChange={(e) => assignGuest(g.id, e.target.value)}
                  className="z-input" style={{ width: 110, padding: "4px 6px", margin: 0, fontSize: 12 }}
                  data-testid={`guest-assign-${g.id}`}
                >
                  <option value="">Non placé</option>
                  {plan.tables.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                <button className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error)", padding: 4 }} onClick={() => removeGuest(g.id)} data-testid={`guest-remove-${g.id}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add table modal */}
      <Modal open={showAddTable} onClose={() => setShowAddTable(false)} title="Ajouter une table" testId="add-table-modal">
        <ZInput label="Nom de la table" value={tableForm.label} onChange={(e) => setTableForm({ ...tableForm, label: e.target.value })} placeholder="Table 1, Table d'honneur…" data-testid="table-form-label" />
        <div style={{ display: "flex", gap: 10 }}>
          <ZSelect label="Forme" value={tableForm.shape} onChange={(e) => setTableForm({ ...tableForm, shape: e.target.value })} data-testid="table-form-shape">
            <option value="round">Ronde</option>
            <option value="rect">Rectangulaire</option>
          </ZSelect>
          <ZInput label="Nombre de couverts" type="number" min="1" max="60" value={tableForm.seats} onChange={(e) => setTableForm({ ...tableForm, seats: e.target.value })} data-testid="table-form-seats" />
        </div>
        <label className="z-label">Couleur</label>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          {COLORS.map((c) => (
            <button key={c} type="button" onClick={() => setTableForm({ ...tableForm, color: c })} style={{ width: 28, height: 28, borderRadius: "50%", background: c, border: tableForm.color === c ? "3px solid #111" : "2px solid #fff", cursor: "pointer" }} data-testid={`table-color-${c}`} />
          ))}
        </div>
        <ZButton block onClick={addTable} data-testid="table-form-submit">Ajouter</ZButton>
      </Modal>

      {/* Share modal */}
      <Modal open={showShare} onClose={() => setShowShare(false)} title="Partager le plan de table" testId="share-modal">
        <p style={{ fontSize: 14, color: "var(--z-muted)", marginBottom: 12 }}>
          Activez le partage pour que vos invités retrouvent leur table via un lien ou un QR code.
        </p>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={!!plan.is_public} onChange={togglePublic} data-testid="share-public-toggle" />
          <span>Rendre le plan public</span>
        </label>
        {plan.is_public && plan.public_url && (
          <div style={{ textAlign: "center" }}>
            <div style={{ background: "#fff", padding: 12, display: "inline-block", borderRadius: 12 }}>
              <QRCodeCanvas value={plan.public_url} size={150} data-testid="share-qr" />
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
              <input className="z-input" readOnly value={plan.public_url} data-testid="share-link" />
              <ZButton size="sm" onClick={copyLink} data-testid="share-copy-btn">{copied ? <Check size={15} /> : <Copy size={15} />}</ZButton>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
